import angularModule from 'AddToCalendarWidget/angularModule/scripts/add_to_calendar_widget_module';
import * as userAgentHelper from 'userAgentHelper';
import { OuiBuilder, OuiEvent } from 'OuiCalendarEvent';
import { saveAs } from 'file-saver';
import template from 'AddToCalendarWidget/angularModule/views/add_to_calendar_widget.html';
import cacheAngularTemplate from 'cacheAngularTemplate';
import { addEventToDeviceCalendar } from 'DeviceCalendar';

const templateUrl = cacheAngularTemplate(angularModule, template);

angularModule.directive('addToCalendarWidget', [
    '$injector',
    function factory($injector) {
        const $window = $injector.get('$window');
        const NavigationHelperMixin = $injector.get('Navigation.NavigationHelperMixin');

        return {
            restrict: 'E',
            scope: {
                eventId: '<',
                eventTitle: '<',
                eventStartDate: '<',
                eventEndDate: '<',
                eventUrl: '<?',
                eventDescription: '<?',
                eventAddress: '<?',
                eventAllDay: '<?',
                mobileLabelLocale: '@?',
                desktopLabelLocale: '@?',
            },
            templateUrl,
            link(scope) {
                //----------------------------
                // Initialization
                //----------------------------

                scope.mobileLabelLocale =
                    scope.mobileLabelLocale || 'add_to_calendar_widget.add_to_calendar_widget.add_to_calendar';
                scope.desktopLabelLocale =
                    scope.desktopLabelLocale || 'add_to_calendar_widget.add_to_calendar_widget.add_to_calendar_select';

                scope.modelProxy = {
                    showCalendarOptions: false,
                };

                //----------------------------
                // Scope Methods & Helpers
                //----------------------------

                // There may be cases where the parent wants to close the calendar options dropdown
                // when an event occurs. For example, the parent may want the widget dropdown to
                // close if the user clicks outside of it. In this case, the parent can $broadcast
                // an the `closeCalendarOptions` event and this directive will close the dropdown
                // when it registers the event being triggered.
                scope.$on('closeCalendarOptions', () => {
                    scope.closeCalendarOptions();
                });

                scope.closeCalendarOptions = () => {
                    scope.modelProxy.showCalendarOptions = false;
                };

                const calendarEvent = new OuiEvent({
                    id: scope.eventId,
                    title: scope.eventTitle,
                    startDate: scope.eventStartDate,
                    endDate: scope.eventEndDate,
                    url: scope.eventUrl,
                    description: scope.eventDescription,
                    address: scope.eventAddress,
                });
                scope.calendarEvent = calendarEvent; // Note: put on scope for testing

                // In Cordova we'll use a calendar plugin
                scope.isCordova = !!$window.CORDOVA;

                // If we detect a mobile or tablet device, but we aren't in Cordova, then we assume
                // we are in a mobile or tablet browser. In this case we'll just show a button that
                // will download an ICS. Chrome and mobile Safari will directly
                // launch the system calendar. Others, like Firefox, will download the ICS file, which
                // can then be tapped to open the system calendar.
                scope.isMobileOrTabletBrowser = !$window.CORDOVA && userAgentHelper.isMobileOrTabletDevice();

                // We use this check to conditionally hide the functionality altogether because
                // saveAs, which is used to download the ICS file, has some documented issues in iOS and iPadOS.
                // See https://github.com/eligrey/FileSaver.js#ios
                // See https://github.com/eligrey/FileSaver.js/issues/581
                // See https://github.com/eligrey/FileSaver.js/issues/586
                scope.isiOSoriPadOS = userAgentHelper.isiOSoriPadOSDevice();

                // Otherwise, in desktop browsers we'll give options to open Google or Microsoft
                // calendar http links, or just download the ICS for opening in a desktop program.

                scope.addToCalendar = calendarMethod => {
                    if (calendarMethod === 'cordova') {
                        addEventToDeviceCalendar({ ...calendarEvent, allDay: !!scope.eventAllDay }).then(added => {
                            if (!added) $window.alert(`Failed to add ${calendarEvent.title} to calendar.`);
                        });
                    } else if (calendarMethod === 'google') {
                        NavigationHelperMixin.loadUrl(OuiBuilder.google(calendarEvent), '_blank');
                    } else if (calendarMethod === 'office365') {
                        // Office 365 (paid) Outlook accounts
                        NavigationHelperMixin.loadUrl(OuiBuilder.outlook(calendarEvent, 'office'), '_blank');
                    } else if (calendarMethod === 'outlookLive') {
                        // Personal (free) Outlook accounts
                        NavigationHelperMixin.loadUrl(OuiBuilder.outlook(calendarEvent, 'live'), '_blank');
                    } else if (calendarMethod === 'ics') {
                        const icsBlob = OuiBuilder.ics(calendarEvent, 'Quantic Holdings, Inc.', 'blob');
                        const hyphenatedTitle = calendarEvent.title.replace(/ +/g, '-').toLowerCase(); // https://stackoverflow.com/a/4310084/1747491
                        saveAs(icsBlob, `${hyphenatedTitle}.ics`);
                    }

                    scope.closeCalendarOptions();
                };
            },
        };
    },
]);
